import { useMutation, type UseMutateAsyncFunction, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

interface ReturnPayload {
  comments: string;
}

async function returnTrip({ tripId, comment }: { tripId: string; comment: string }): Promise<void> {
  const response = await apiFetch(`/api/v1/business-trip-requests/${tripId}/return-back`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ comments: comment } satisfies ReturnPayload),
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message =
      payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)
        ? String(
            (payload as Record<string, { message?: string }>).error?.message ??
              'Failed to return trip back'
          )
        : 'Failed to return trip back';

    throw new Error(message);
  }
}

interface ReturnVariables {
  tripId: string;
  comment: string;
}

interface ReturnMutation {
  mutate: UseMutateAsyncFunction<void, Error, ReturnVariables, unknown>;
  isPending: boolean;
}

export function useReturnTrip(): ReturnMutation {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: returnTrip,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripApprovals });
      if (variables?.tripId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.trip(variables.tripId) });
      }
    },
    onError: (error) => {
      console.error('Error returning trip back:', error);
    },
  });

  return {
    mutate: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
